import { parseNonEmptyStrings } from '~/utils/societePayloadBuilder/parseHelpers';

import { CTA_PROMOTION, FLIGHT_VOUCHER_PROMOTION, PROMO_CODE_PROMOTION } from '../constants';
import { buildOptionalExpiresAt, payloadText, previewExpiresAt, previewRequiredValue } from '../payloadFields';

import { createDefaultPromoCardCarouselState } from './defaults';
import type { LuxPlusPromoCard, LuxPlusPromoCardCarouselState } from './types';

function omitUndefined<T extends Record<string, unknown>>(value: T): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined));
}

function buildTypeFields(promo: LuxPlusPromoCard, preview: boolean): Record<string, unknown> {
  if (promo.type === PROMO_CODE_PROMOTION) {
    const primaryActionText = payloadText(promo.primaryActionText);
    const primaryActionHref = payloadText(promo.primaryActionHref);

    return omitUndefined({
      promoCode: preview ? previewRequiredValue(promo.promoCode) : payloadText(promo.promoCode),
      termsAndConditions: preview
        ? previewRequiredValue(promo.termsAndConditions)
        : payloadText(promo.termsAndConditions),
      primaryActionText: primaryActionText || undefined,
      primaryActionHref: primaryActionHref || undefined,
    });
  }

  if (promo.type === CTA_PROMOTION) {
    return {
      ctaText: preview ? previewRequiredValue(promo.ctaText) : payloadText(promo.ctaText),
      ctaLink: preview ? previewRequiredValue(promo.ctaLink) : payloadText(promo.ctaLink),
    };
  }

  return {};
}

function buildPromo(promo: LuxPlusPromoCard, preview: boolean): Record<string, unknown> {
  const expiresAt = preview ? previewExpiresAt(promo.expiresAt) : buildOptionalExpiresAt(promo.expiresAt);

  return omitUndefined({
    type: promo.type === FLIGHT_VOUCHER_PROMOTION ? FLIGHT_VOUCHER_PROMOTION : promo.type,
    label: preview ? previewRequiredValue(promo.label) : payloadText(promo.label),
    title: preview ? previewRequiredValue(promo.title) : payloadText(promo.title),
    subtitle: preview ? previewRequiredValue(promo.subtitle) : payloadText(promo.subtitle),
    expiresAt,
    eligibleRegions: parseNonEmptyStrings(promo.eligibleRegions),
    ...buildTypeFields(promo, preview),
  });
}

export function buildPromoCardCarouselPayload(state: LuxPlusPromoCardCarouselState): Record<string, unknown> {
  return {
    promos: state.promos.map((promo) => buildPromo(promo, false)),
  };
}

export function buildPromoCardCarouselPreviewPayload(state: LuxPlusPromoCardCarouselState): Record<string, unknown> {
  const promos = state.promos.length > 0 ? state.promos : createDefaultPromoCardCarouselState().promos;

  return {
    promos: promos.map((promo) => buildPromo(promo, true)),
  };
}

export function serializePromoCardCarouselPayload(state: LuxPlusPromoCardCarouselState): string {
  return JSON.stringify(buildPromoCardCarouselPayload(state), null, 2);
}

export function serializePromoCardCarouselPreviewPayload(state: LuxPlusPromoCardCarouselState): string {
  return JSON.stringify(buildPromoCardCarouselPreviewPayload(state), null, 2);
}

export function promoCardCarouselPayloadDownloadFilename(): string {
  return 'luxplus-promo-card-carousel-payload.txt';
}
